import { useCallback, useEffect, useRef } from 'react';
import { UseNavigateResult } from '@tanstack/react-router';
import { Authn, HandleCallbackProps } from '@frontend/auth';
import { SignInMethod } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { hydraShellService } from '../services';
import { authFlowTimer, recordAuthFlowSuccess } from '../services/auth-flow-timer';
import { recordAuthResult, restoreAuthFlowOrigin } from '../services/auth-metrics';
import { getRedirectURL } from '../services/fetch-wrapper.ts';
import { handlePostAuthRedirect } from '../services/route-utils';
import { ContentLoader } from './content-loader.tsx';
import { FormWrap } from './form-wrap';

interface Props<T extends string> {
  auth: Authn;
  navigate: UseNavigateResult<T>;
  signInMethod: SignInMethod;
  getAuthCallbackProps?: () => HandleCallbackProps | undefined;
}

export const SignInCallback = <T extends string>({ auth, navigate, signInMethod, getAuthCallbackProps }: Props<T>) => {
  const { t } = useTranslation('SignIn');
  const hasStarted = useRef(false);

  const handleCallback = useCallback(async () => {
    // before the accept flow below clears the login challenge
    restoreAuthFlowOrigin(hydraShellService.getLoginChallenge());
    let redirectURL = appConfig.DEFAULT_REDIRECT;
    try {
      const initialLoginResponse = await hydraShellService.initializeOAuth2Flow();
      redirectURL = !!initialLoginResponse.requestUrl
        ? getRedirectURL(initialLoginResponse.requestUrl) ?? appConfig.DEFAULT_REDIRECT
        : appConfig.DEFAULT_REDIRECT;
    } catch (error) {
      console.error(error);
    }

    // Always exchange the code and accept the hydra OAuth2 flow, even if the browser
    // already has an authenticated session. In multi-user shared-browser scenarios
    // (e.g. user A logs out of the desktop app but their web session is still live,
    // then user B signs in from the desktop) skipping these steps means we never call
    // acceptOAuth2Flow and never get the correct redirectTo (the desktop's weave://...
    // callback). The caller of this page should always carry a fresh OAuth code.
    auth.changeAuthMethod(signInMethod);

    let callbackProps: HandleCallbackProps | undefined = undefined;
    if (getAuthCallbackProps) {
      callbackProps = getAuthCallbackProps();
    }

    // tracks which step failed so IdP code-exchange failures can be told apart from hydra accept failures
    let failureReason = 'handle_callback_failed';
    try {
      await auth.handleCallback(callbackProps);
      failureReason = 'accept_flow_failed';
      const acceptLoginResponse = await hydraShellService.acceptOAuth2Flow();
      recordAuthResult({ method: signInMethod, outcome: 'success' });
      recordAuthFlowSuccess(signInMethod);
      handlePostAuthRedirect(acceptLoginResponse.redirectTo || appConfig.DEFAULT_REDIRECT, navigate, {
        reason: `acceptLoginResponse for ${signInMethod}`,
      });
    } catch (e: any) {
      console.error(e);
      recordAuthResult({ method: signInMethod, outcome: 'fail', failureReason });
      authFlowTimer.recordFailedAttempt();
      handlePostAuthRedirect(redirectURL || appConfig.DEFAULT_REDIRECT, navigate, {
        reason: `error caught in sign-in callback ${signInMethod}`,
        error: e,
      });
    }
  }, [auth, getAuthCallbackProps, navigate, signInMethod]);

  useEffect(() => {
    // the OAuth code is single-use and okta-auth-js clears it from the URL once parsed, so never handle it twice
    // (StrictMode double-invokes effects in dev)
    if (hasStarted.current) {
      return;
    }
    hasStarted.current = true;
    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormWrap>
      <ContentLoader message={t('Signing in...')} show />
    </FormWrap>
  );
};
